import { useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import {
  Flex,
  Heading,
  Link,
  Nav,
  NavBody,
  NavHeader,
  NavItem,
  NavList,
  Sidebar,
} from '@optiaxiom/react';
import { IconComments, IconDroplet, IconEnvelope } from '@optimizely/axiom-icons';
import { useDecision } from '@optimizely/react-sdk';

import { PageHeader } from '@frontend/components';
import { FeatureFlag } from '@frontend/contexts';
import { useNamespacedNavigate, useOperations } from '@frontend/hooks';

import ChatSetupConfiguration from './chat/components/ChatSetupConfiguration';
import EmailDomainConfiguration from './email-domain/components/EmailDomainConfiguration';
import { AppearanceConfiguration } from './theme';

type SettingsSection = {
  title: string;
  description: React.ReactNode | string;
};

const settingsConfig: Record<string, SettingsSection> = {
  email: {
    title: 'Email Domain Configuration',
    description: (
      <>
        Configure email subdomain prefix for workflow triggers and email sending.{' '}
        <Link
          external
          href="https://support.optimizely.com/hc/en-us/articles/36359944449805-Get-started-with-Optimizely-Opal-for-administrators"
        >
          Learn more.
        </Link>
      </>
    ),
  },
  chat: {
    title: 'Chat Configuration',
    description: (
      <>
        Customize your Opal chat experience across all Optimizely products.{' '}
        <Link
          external
          href="https://support.optimizely.com/hc/en-us/articles/36359944449805-Get-started-with-Optimizely-Opal-for-administrators"
        >
          Learn more.
        </Link>
      </>
    ),
  },
  appearance: {
    title: 'Appearance',
    description: 'Personalize your Opal App experience.',
  },
};

function SettingsPage() {
  const location = useLocation();
  const navigate = useNamespacedNavigate();
  const { canUser } = useOperations();

  const isAdmin = canUser('manage_instances');
  const [appearance_settings_decision] = useDecision('appearance_settings');

  // Default route: admins go to chat, users go to appearance
  useEffect(() => {
    if (location.pathname.endsWith('/settings') || location.pathname.endsWith('/settings/')) {
      const defaultRoute = isAdmin
        ? '/settings/chat'
        : appearance_settings_decision.enabled
          ? '/settings/appearance'
          : '/chat';
      navigate(defaultRoute, { replace: true });
    }
  }, [location.pathname, navigate, isAdmin, appearance_settings_decision]);

  const getCurrentSection = (): string => {
    if (location.pathname.includes('/email')) return 'email';
    if (location.pathname.includes('/chat')) return 'chat';
    if (location.pathname.includes('/appearance')) return 'appearance';
    return 'default';
  };

  const currentSection = getCurrentSection();
  const headerConfig = settingsConfig[currentSection] || {
    title: 'Settings',
    description: 'Manage your application settings.',
  };

  const onSelectNavItem = (navItem: string) => {
    navigate(`/settings/${navItem}`);
  };

  return (
    <Flex flexDirection="row" gap="32" w="full" h="full">
      {/* SIDEBAR - Full Height */}
      <Flex h="full">
        <Sidebar defaultExpanded>
          <Nav>
            <NavHeader>
              <Heading color="fg.default" fontSize="lg" fontWeight="600">
                Opal Configurations
              </Heading>
            </NavHeader>
            <NavBody>
              <NavList>
                {isAdmin && (
                  <NavItem
                    active={currentSection === 'chat'}
                    icon={<IconComments />}
                    onClick={() => onSelectNavItem('chat')}
                  >
                    Chat
                  </NavItem>
                )}
                {isAdmin && (
                  <FeatureFlag featureKey="workflow-agent">
                    <NavItem
                      active={currentSection === 'email'}
                      icon={<IconEnvelope />}
                      onClick={() => onSelectNavItem('email')}
                    >
                      Email Domain
                    </NavItem>
                  </FeatureFlag>
                )}
                <FeatureFlag featureKey="appearance_settings">
                  <NavItem
                    active={currentSection === 'appearance'}
                    icon={<IconDroplet />}
                    onClick={() => onSelectNavItem('appearance')}
                  >
                    Appearance
                  </NavItem>
                </FeatureFlag>
              </NavList>
            </NavBody>
          </Nav>
        </Sidebar>
      </Flex>

      {/* CONTENT AREA - Header + Main Content */}
      <Flex flex="1" h="full" flexDirection="column" pr="32" pt="24" pb="24">
        {/* DYNAMIC HEADER */}
        <Flex gap="8" w="full">
          <PageHeader title={headerConfig.title} headingText={headerConfig.description} />
        </Flex>
        {/* MAIN CONTENT */}
        <Flex flex="1" pt="0">
          <Routes>
            <Route path="/chat" element={<ChatSetupConfiguration />} />
            <Route path="/email" element={<EmailDomainConfiguration />} />
            <Route path="/appearance" element={<AppearanceConfiguration />} />
          </Routes>
        </Flex>
      </Flex>
    </Flex>
  );
}

export default SettingsPage;
